/**
 * The in-app assistant's API (backend/src/routes/assistant.ts).
 *
 * Chat and confirm stream server-sent events over a POST, which axios can't
 * read incrementally, so those two use fetch with the same session cookie and
 * CSRF double-submit token the axios client sends.
 */
import { apiClient, warmCsrf } from './api'
import { apiUrl } from './apiUrl'
import { getCsrfToken, setCsrfToken } from './csrfToken'

export type ConfirmKind = 'contacts_customer' | 'money' | 'deletes' | 'settings'

export interface AssistantUsage {
  paused: boolean
  nearLimit: boolean
  percentUsed: number | null
  /** YYYY-MM-DD, the business's time zone. */
  resetsOn: string
}

export type AssistantEvent =
  | { type: 'conversation'; id: string; title: string }
  | { type: 'text'; delta: string }
  | { type: 'tool_call'; id: string; name: string; title: string; input: Record<string, unknown> }
  | { type: 'tool_result'; id: string; name: string; ok: boolean; summary: string }
  | { type: 'confirm'; id: string; name: string; title: string; kind: ConfirmKind; input: Record<string, unknown>; target?: string }
  | ({ type: 'usage' } & AssistantUsage)
  | { type: 'done' }
  | { type: 'error'; message: string }

/** What the panel shows (GET /conversations/:id returns the same shapes). */
export type TranscriptItem =
  | { type: 'user'; text: string }
  | { type: 'assistant'; text: string }
  | { type: 'tool'; id: string; name: string; title?: string; status: 'running' | 'ok' | 'error' | 'cancelled' | 'awaiting'; summary: string }
  | { type: 'confirm'; id: string; name: string; title: string; kind: ConfirmKind; input: Record<string, unknown>; target?: string; deciding?: boolean }
  | { type: 'error'; text: string }

export interface ConversationSummary {
  id: string
  title: string
  updatedAt: string
}

export async function fetchAssistantStatus(): Promise<{ configured: boolean; usage: AssistantUsage }> {
  const res = await apiClient.get('/api/assistant/status')
  return res.data.data
}

export async function fetchConversations(): Promise<ConversationSummary[]> {
  const res = await apiClient.get('/api/assistant/conversations')
  return res.data.data
}

export async function fetchConversation(id: string): Promise<{ id: string; title: string; items: TranscriptItem[] }> {
  const res = await apiClient.get(`/api/assistant/conversations/${encodeURIComponent(id)}`)
  return res.data.data
}

export async function archiveConversation(id: string): Promise<void> {
  await apiClient.delete(`/api/assistant/conversations/${encodeURIComponent(id)}`)
}

/** A refusal before the stream starts (paused, not set up, rate limited). */
export class AssistantRequestError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public usage?: AssistantUsage,
  ) {
    super(message)
  }
}

/** Parse SSE frames out of a growing buffer; returns the unconsumed tail. */
export function drainSse(buffer: string, onEvent: (e: AssistantEvent) => void): string {
  let rest = buffer
  let idx = rest.indexOf('\n\n')
  while (idx !== -1) {
    const frame = rest.slice(0, idx)
    rest = rest.slice(idx + 2)
    for (const line of frame.split('\n')) {
      if (!line.startsWith('data: ')) continue
      try {
        onEvent(JSON.parse(line.slice(6)) as AssistantEvent)
      } catch {
        /* a malformed frame is skipped, never fatal */
      }
    }
    idx = rest.indexOf('\n\n')
  }
  return rest
}

async function postStream(path: string, body: unknown, onEvent: (e: AssistantEvent) => void, signal?: AbortSignal, retried = false): Promise<void> {
  await warmCsrf()
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream', 'x-csrf-token': getCsrfToken() ?? '' },
    body: JSON.stringify(body),
    signal,
  })
  if (!res.ok || !res.body) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string; usage?: AssistantUsage }
    if (res.status === 403 && !retried && /CSRF/i.test(data.error ?? '')) {
      setCsrfToken(null)
      return postStream(path, body, onEvent, signal, true)
    }
    if (res.status === 401) window.location.assign('/login')
    throw new AssistantRequestError(
      data.error ?? (res.status === 429 ? 'Too many requests. Try again in a minute.' : 'The assistant is unavailable right now.'),
      res.status,
      data.code,
      data.usage,
    )
  }
  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  for (;;) {
    const { value, done } = await reader.read()
    if (done) break
    buffer = drainSse(buffer + decoder.decode(value, { stream: true }), onEvent)
  }
  drainSse(`${buffer}\n\n`, onEvent)
}

export function streamChat(input: { message: string; conversationId?: string | null }, onEvent: (e: AssistantEvent) => void, signal?: AbortSignal) {
  return postStream('/api/assistant/chat', { message: input.message, ...(input.conversationId ? { conversationId: input.conversationId } : {}) }, onEvent, signal)
}

export function streamDecision(conversationId: string, toolUseId: string, decision: 'confirm' | 'cancel', onEvent: (e: AssistantEvent) => void) {
  return postStream(`/api/assistant/conversations/${encodeURIComponent(conversationId)}/confirm`, { toolUseId, decision }, onEvent)
}

// ─── Drafting ────────────────────────────────────────────────────────────────

export type DraftTone = 'friendly' | 'professional' | 'brief'

export type DraftRequest =
  | { kind: 'quote_scope'; lineItems: Array<{ description: string; quantity?: number; unit?: string | null }>; serviceCategoryId?: string | null; sketch?: unknown }
  | { kind: 'message_reply'; contactId: string; channel: 'sms' | 'email' }
  | { kind: 'quote_follow_up'; quoteId: string; channel: 'sms' | 'email' }
  | { kind: 'job_notes_summary'; jobId: string }
  | { kind: 'review_request'; contactId: string; jobId?: string }

export interface DraftOptions {
  tone?: DraftTone
  current?: string
  previous?: string
  instructions?: string
}

export async function requestDraft(req: DraftRequest, opts: DraftOptions = {}): Promise<string> {
  const res = await apiClient.post('/api/assistant/drafts', { ...req, ...opts })
  return res.data.data.text as string
}

// ─── Words for the panel ─────────────────────────────────────────────────────

export const CONFIRM_COPY: Record<ConfirmKind, { badge: string; action: string }> = {
  contacts_customer: { badge: 'Contacts a customer', action: 'Send' },
  money: { badge: 'Money', action: 'Confirm' },
  deletes: { badge: 'Removes something', action: 'Confirm' },
  settings: { badge: 'Changes settings', action: 'Save change' },
}

/** "2026-11-01" → "Sunday, November 1" (a date-only value, no time zone shift). */
export function formatResetDate(day: string): string {
  const [y, m, d] = day.split('-').map(Number)
  if (!y || !m || !d) return day
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

/** Field names → words, for the confirmation card. Ids are left out. */
export function confirmFields(input: Record<string, unknown>): Array<{ label: string; value: string; long: boolean }> {
  const out: Array<{ label: string; value: string; long: boolean }> = []
  for (const [key, raw] of Object.entries(input)) {
    if (raw === undefined || raw === null || raw === '') continue
    if (/(^id$|Id$|Ids$|idempotencyKey|^confirm(_send)?$)/.test(key)) continue
    const label = key
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/^./, (c) => c.toUpperCase())
    const value = typeof raw === 'string' ? raw : typeof raw === 'number' || typeof raw === 'boolean' ? String(raw) : JSON.stringify(raw, null, 1)
    out.push({ label, value, long: value.length > 60 || /\n/.test(value) })
  }
  return out
}

// ── Proactive (backend services/assistant/proactive.ts) ─────────────────────

export type NudgeCategory = 'leads' | 'quotes' | 'jobs' | 'money' | 'inbox' | 'automations' | 'brief'

export interface Nudge {
  id: string
  category: NudgeCategory
  kind: string
  text: string
  ask: string | null
  actionLabel: string | null
  link: string | null
  createdAt: string
}

export type NudgeAction = 'do' | 'done' | 'snooze' | 'dismiss' | 'mute'

export interface ProactiveSettings {
  categories: Record<NudgeCategory, boolean>
  quietStart: string
  quietEnd: string
  maxPerDay: number
  push: boolean
  briefHour: number
  muted: string[]
}

export interface ProactiveState {
  enabled: boolean
  settings: ProactiveSettings
  houseRules: string
  canManage: boolean
}

export async function fetchNudges(): Promise<Nudge[]> {
  const res = await apiClient.get('/api/assistant/nudges')
  return res.data.data
}

export async function actOnNudge(id: string, action: NudgeAction): Promise<{ ask: string | null; changed: boolean }> {
  const res = await apiClient.post(`/api/assistant/nudges/${encodeURIComponent(id)}`, { action })
  return res.data.data
}

export async function fetchProactive(): Promise<ProactiveState> {
  const res = await apiClient.get('/api/assistant/proactive')
  return res.data.data
}

export async function saveProactive(
  patch: { enabled?: boolean; houseRules?: string; settings?: Partial<Omit<ProactiveSettings, 'categories'>> & { categories?: Partial<ProactiveSettings['categories']> } },
): Promise<ProactiveState> {
  const res = await apiClient.patch('/api/assistant/proactive', patch)
  return res.data.data
}
